import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type {
  GetRoutineBriefingResponseBody,
  GetRoutineResponseBody,
  GetRoutineRunsResponseBody,
  GetRoutinesResponseBody,
  RoutineAgentWireType,
  RoutineRunWireType,
  RoutineTemplateWireType,
  RoutineWireType,
  SetRoutineAgentEnabledResponseBody,
  StartSetupConversationResponseBody,
  UpdateRoutineBody,
} from "@app/types/api/me/routines";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher } from "swr";

export function buildRoutinesUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/me/routines`;
}

export function useRoutines({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const routinesFetcher: Fetcher<GetRoutinesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildRoutinesUrl(owner.sId),
    routinesFetcher,
    { disabled }
  );

  return {
    routines: data?.routines ?? emptyArray<RoutineWireType>(),
    templates: data?.templates ?? emptyArray<RoutineTemplateWireType>(),
    agents: data?.agents ?? emptyArray<RoutineAgentWireType>(),
    isRoutinesLoading: !disabled && !error && !data,
    isRoutinesError: !!error,
    mutateRoutines: mutate,
  };
}

export function useRoutine({
  owner,
  routineId,
  disabled,
}: {
  owner: LightWorkspaceType;
  routineId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const routineFetcher: Fetcher<GetRoutineResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    routineId ? `${buildRoutinesUrl(owner.sId)}/${routineId}` : null,
    routineFetcher,
    { disabled }
  );

  return {
    routine: data?.routine ?? null,
    isRoutineLoading: !disabled && !!routineId && !error && !data,
    isRoutineError: !!error,
    mutateRoutine: mutate,
  };
}

export function useRoutineRuns({
  owner,
  routineId,
  disabled,
}: {
  owner: LightWorkspaceType;
  routineId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const runsFetcher: Fetcher<GetRoutineRunsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    routineId ? `${buildRoutinesUrl(owner.sId)}/${routineId}/runs` : null,
    runsFetcher,
    { disabled }
  );

  return {
    runs: data?.runs ?? emptyArray<RoutineRunWireType>(),
    isRunsLoading: !disabled && !!routineId && !error && !data,
    isRunsError: !!error,
    mutateRuns: mutate,
  };
}

/** The newest briefing a routine produced, as Home shows it. */
export function useLatestBriefing({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const briefingFetcher: Fetcher<GetRoutineBriefingResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `${buildRoutinesUrl(owner.sId)}/briefing`,
    briefingFetcher,
    { disabled }
  );

  return {
    briefingRun: data?.run ?? null,
    briefingBody: data?.body ?? null,
    isBriefingLoading: !disabled && !error && !data,
    mutateBriefing: mutate,
  };
}

async function post<T>({
  url,
  method,
  body,
  failureTitle,
  sendNotification,
}: {
  url: string;
  method: "POST" | "PATCH" | "DELETE";
  body?: unknown;
  failureTitle: string;
  sendNotification: ReturnType<typeof useSendNotification>;
}): Promise<Result<T, Error>> {
  try {
    const res = await clientFetch(url, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      const errorData = await getErrorFromResponse(res);
      sendNotification({
        type: "error",
        title: failureTitle,
        description: errorData.message,
      });
      return new Err(new Error(errorData.message));
    }

    return new Ok((await res.json()) as T);
  } catch (e) {
    const error = normalizeError(e);
    sendNotification({
      type: "error",
      title: failureTitle,
      description: error.message,
    });
    return new Err(error);
  }
}

export function useRoutineActions({ owner }: { owner: LightWorkspaceType }) {
  const sendNotification = useSendNotification();
  const { mutateRoutines } = useRoutines({ owner, disabled: true });

  const createFromTemplate = useCallback(
    async (templateId: string, timezone?: string) => {
      const result = await post<GetRoutineResponseBody>({
        url: buildRoutinesUrl(owner.sId),
        method: "POST",
        body: {
          templateId,
          timezone:
            timezone ??
            Intl.DateTimeFormat().resolvedOptions().timeZone ??
            "Etc/UTC",
        },
        failureTitle: "Failed to add the routine",
        sendNotification,
      });
      await mutateRoutines();
      return result;
    },
    [mutateRoutines, owner.sId, sendNotification]
  );

  const updateRoutine = useCallback(
    async (routineId: string, update: UpdateRoutineBody) => {
      const result = await post<GetRoutineResponseBody>({
        url: `${buildRoutinesUrl(owner.sId)}/${routineId}`,
        method: "PATCH",
        body: update,
        failureTitle: "Failed to update the routine",
        sendNotification,
      });
      await mutateRoutines();
      return result;
    },
    [mutateRoutines, owner.sId, sendNotification]
  );

  const removeRoutine = useCallback(
    async (routineId: string) => {
      const result = await post<{ success: true }>({
        url: `${buildRoutinesUrl(owner.sId)}/${routineId}`,
        method: "DELETE",
        failureTitle: "Failed to remove the routine",
        sendNotification,
      });
      await mutateRoutines();
      return result;
    },
    [mutateRoutines, owner.sId, sendNotification]
  );

  const runNow = useCallback(
    async (routineId: string) => {
      const result = await post<{ success: true }>({
        url: `${buildRoutinesUrl(owner.sId)}/${routineId}/run`,
        method: "POST",
        failureTitle: "Failed to start the run",
        sendNotification,
      });
      if (result.isOk()) {
        sendNotification({
          type: "success",
          title: "Run started",
          description: "It shows up under Runs once the session opens.",
        });
      }
      return result;
    },
    [owner.sId, sendNotification]
  );

  const startSetupConversation = useCallback(
    async (routineId: string) => {
      return post<StartSetupConversationResponseBody>({
        url: `${buildRoutinesUrl(owner.sId)}/${routineId}/setup-conversation`,
        method: "POST",
        failureTitle: "Failed to open the setup conversation",
        sendNotification,
      });
    },
    [owner.sId, sendNotification]
  );

  // `agentKey` is a teammate's agent id, or a hired agent's hire key.
  const setAgentEnabled = useCallback(
    async (agentKey: string, enabled: boolean) => {
      const result = await post<SetRoutineAgentEnabledResponseBody>({
        url: `${buildRoutinesUrl(owner.sId)}/agents/${agentKey}`,
        method: "PATCH",
        body: { enabled },
        failureTitle: enabled
          ? "Failed to switch the agent on"
          : "Failed to switch the agent off",
        sendNotification,
      });
      await mutateRoutines();
      return result;
    },
    [mutateRoutines, owner.sId, sendNotification]
  );

  const retryRun = useCallback(
    async (routineId: string, conversationId: string) => {
      const result = await post<{ success: true }>({
        url: `${buildRoutinesUrl(owner.sId)}/${routineId}/runs/${conversationId}/retry`,
        method: "POST",
        failureTitle: "Failed to retry the run",
        sendNotification,
      });
      if (result.isOk()) {
        sendNotification({
          type: "success",
          title: "Looking again",
          description: "The same session picks up whatever it finds.",
        });
      }
      return result;
    },
    [owner.sId, sendNotification]
  );

  const setWebhook = useCallback(
    async (routineId: string, enabled: boolean) => {
      const result = await post<GetRoutineResponseBody>({
        url: `${buildRoutinesUrl(owner.sId)}/${routineId}/webhook`,
        method: "POST",
        body: { enabled },
        failureTitle: "Failed to change the webhook",
        sendNotification,
      });
      await mutateRoutines();
      return result;
    },
    [mutateRoutines, owner.sId, sendNotification]
  );

  return {
    createFromTemplate,
    updateRoutine,
    removeRoutine,
    runNow,
    retryRun,
    setWebhook,
    startSetupConversation,
    setAgentEnabled,
  };
}
